import { useState } from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { TagCategoryForm } from '../../../components/tags';
import { updateTagCategory, ApiError } from '../../../lib/apiClient';
import {
  buildValidationFromForm,
  type TagCategoryFormData,
} from '../../../components/tags/TagCategoryForm';
import type { TagValueType, TagValueValidation } from '../../../graphql/types';
import { useAppSelector } from '../../../store';
import { selectUser } from '../../../store/features/auth/authSlice';
import { canManageTagCategory } from '../../../lib/permissions';
import '@tylertech/forge/icon';
import '@tylertech/forge/banner';

export const Route = createFileRoute('/tags/categories/$categoryId/edit')({
  component: EditTagCategoryPage,
});

export { EditTagCategoryPage };

interface StoredTagCategory {
  entityId: string;
  name: string;
  description?: string | null;
  scope: 'global' | 'team';
  scopeOwnerId?: string | null;
  color?: string | null;
  allowMultiple: boolean;
  valueType?: TagValueType | null;
  validation?: TagValueValidation | null;
}

function EditTagCategoryPage(): JSX.Element {
  const { categoryId } = Route.useParams();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currentUser = useAppSelector(selectUser);

  const IconElement = 'forge-icon' as React.ElementType;
  const BannerElement = 'forge-banner' as React.ElementType;

  // CategoryId carries enough info if passed via route state; otherwise fetch from store/cache
  const category = Route.useRouteContext() as { category?: StoredTagCategory } | undefined;
  const cat = (category as { category?: StoredTagCategory })?.category;

  const userTeams =
    currentUser?.primaryContactForTeams?.map((teamId) => ({
      id: teamId,
      name: teamId,
    })) ?? [];

  if (!cat) {
    return (
      <div className="edit-tag-category-page">
        <p>Loading...</p>
      </div>
    );
  }

  if (
    !canManageTagCategory(currentUser, {
      scope: cat.scope,
      scopeOwnerId: cat.scopeOwnerId ?? undefined,
    })
  ) {
    return (
      <div className="edit-tag-category-page">
        <p>You do not have permission to edit this category.</p>
      </div>
    );
  }

  const handleSubmit = async (data: TagCategoryFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);
    try {
      await updateTagCategory(categoryId, {
        name: data.name,
        description: data.description || undefined,
        color: data.color || undefined,
        allowMultiple: data.allowMultiple,
        // valueType is immutable; only the validation constraints may change.
        validation: buildValidationFromForm(data),
      });
      void navigate({ to: '/tags', search: { page: 1, pageSize: 25, categoryId: '' } });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    void navigate({ to: '/tags', search: { page: 1, pageSize: 25, categoryId: '' } });
  };

  return (
    <div className="edit-tag-category-page">
      <div className="breadcrumb">
        <Link
          to="/tags"
          search={{ page: 1, pageSize: 25, categoryId: '' }}
          className="breadcrumb-link"
        >
          <IconElement name="arrow_back" aria-hidden="true" />
          <span>Back to Tags</span>
        </Link>
      </div>

      <div className="page-header">
        <h1 className="forge-typography--headline4">Edit Tag Category</h1>
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      <TagCategoryForm
        mode="edit"
        defaultValues={{
          name: cat.name,
          description: cat.description ?? undefined,
          scope: cat.scope,
          scopeOwnerId: cat.scopeOwnerId ?? undefined,
          color: cat.color ?? undefined,
          allowMultiple: cat.allowMultiple,
          valueType: cat.valueType ?? 'label-only',
          validationMin: cat.validation?.min ?? undefined,
          validationMax: cat.validation?.max ?? undefined,
          validationRegex: cat.validation?.regex ?? undefined,
        }}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
        currentUser={currentUser}
        userTeams={userTeams}
      />
    </div>
  );
}
